/**
 * Persistent (IndexedDB) cache for E-Hentai / ExHentai gallery HTML.
 *
 * Every proxied HTML request is charged to the Worker's shared egress IP,
 * which e-hentai temp-bans for "excessive request rate". Gallery detail and
 * preview-thumbnail pages barely change, yet the in-memory cache dies with
 * the tab, so reopening a gallery after a reload, a PWA relaunch, or from
 * history re-fetched it. This layer keeps successful responses across
 * sessions, credential-scoped like the in-memory cache, in the `meta` store
 * next to the persisted page-token cache.
 *
 * All operations are best-effort: when IndexedDB is unavailable (Node tests,
 * private mode, blocked storage) reads miss and writes are dropped.
 */

import { getDb } from '@/db/schema';

const KEY_PREFIX = 'eh-html:';
const MAX_ENTRIES = 150;
const PRUNE_EVERY_WRITES = 20;

/** Page-zero gallery HTML: metadata, tags, first thumbnails, comments. */
export const GALLERY_PAGE_ZERO_PERSIST_TTL_MS = 6 * 60 * 60_000;
/** `?p=N` thumbnail pages: page tokens and thumbnails are immutable. */
export const GALLERY_PREVIEW_PAGE_PERSIST_TTL_MS = 24 * 60 * 60_000;

export interface PersistedEhHtml {
  html: string;
  url: string;
  expiresAt: number;
}

interface PersistedEhHtmlRow extends PersistedEhHtml {
  storedAt: number;
}

let writesSincePrune = PRUNE_EVERY_WRITES;

function storageKey(key: string): string {
  return `${KEY_PREFIX}${key}`;
}

function isRow(value: unknown): value is PersistedEhHtmlRow {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<PersistedEhHtmlRow>;
  return (
    typeof row.html === 'string' &&
    typeof row.url === 'string' &&
    typeof row.expiresAt === 'number' &&
    typeof row.storedAt === 'number'
  );
}

export async function readPersistedEhHtml(key: string): Promise<PersistedEhHtml | undefined> {
  try {
    const db = await getDb();
    const row = await db.get('meta', storageKey(key));
    if (!row || !isRow(row.value)) return undefined;
    if (row.value.expiresAt <= Date.now()) {
      await db.delete('meta', storageKey(key)).catch(() => undefined);
      return undefined;
    }
    return { html: row.value.html, url: row.value.url, expiresAt: row.value.expiresAt };
  } catch {
    return undefined;
  }
}

export async function writePersistedEhHtml(
  key: string,
  entry: { html: string; url: string },
  ttlMs: number,
): Promise<void> {
  try {
    const db = await getDb();
    const now = Date.now();
    const value: PersistedEhHtmlRow = { ...entry, expiresAt: now + ttlMs, storedAt: now };
    await db.put('meta', { key: storageKey(key), value });
    writesSincePrune += 1;
    if (writesSincePrune >= PRUNE_EVERY_WRITES) {
      writesSincePrune = 0;
      await prunePersistedEhHtml();
    }
  } catch {
    // Best-effort cache.
  }
}

/** Drop expired rows, then the oldest rows beyond {@link MAX_ENTRIES}. */
export async function prunePersistedEhHtml(): Promise<void> {
  try {
    const db = await getDb();
    const range = IDBKeyRange.bound(KEY_PREFIX, `${KEY_PREFIX}￿`);
    const rows = await db.getAll('meta', range);
    const now = Date.now();
    const live: Array<{ key: string; storedAt: number }> = [];
    const stale: string[] = [];
    for (const row of rows) {
      if (!isRow(row.value) || row.value.expiresAt <= now) stale.push(row.key);
      else live.push({ key: row.key, storedAt: row.value.storedAt });
    }
    live.sort((a, b) => b.storedAt - a.storedAt);
    for (const extra of live.slice(MAX_ENTRIES)) stale.push(extra.key);
    if (stale.length === 0) return;
    const tx = db.transaction('meta', 'readwrite');
    await Promise.all([...stale.map((key) => tx.store.delete(key)), tx.done]);
  } catch {
    // Best-effort cache.
  }
}
